import { FFmpeg } from '@ffmpeg/ffmpeg';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.js';
import config from './config';
import { SessionManager } from './SessionManager';

// Global logging function - will be set by the app
let globalLogFunction: ((message: string, data?: any) => void) | null = null;

// Function to set the global logging function
export const setGlobalLogFunction = (logFn: (message: string, data?: any) => void) => {
  globalLogFunction = logFn;
};

// Helper function to log messages
const logMessage = (message: string, data?: any) => {
  console.log('🔗 AuditioBackend: logMessage called with:', message);
  
  if (globalLogFunction) {
    console.log('🔗 AuditioBackend: Calling global logging function');
    globalLogFunction(message, data);
  } else {
    console.warn('🔗 AuditioBackend: No global logging function available, falling back to console');
    // Fallback to console if global logging isn't set
    console.log(`📝 ${message}`, data || '');
  }
};

// INTERFACES

export interface AudioFile { 
    id: number;
    data: string;
    fileName: string;
    uploadTime: Date;
    youtubeUrl?: string; // YouTube URL for video files
    driveFileId?: string; // Google Drive file ID
    driveFileUrl?: string; // Google Drive file URL
    driveMimeType?: string; // Google Drive MIME type
    cloudFileName?: string; // Cloud Storage filename for production
    localFilePath?: string; // Local file path for development
    ownerDisplayName?: string; // Display name of the file owner
    ownerId?: number; // ID of the file owner
    snippets?: SnippetData[];
}

export interface RegionData {
    id: number;
    start: number;
    end: number;
    content: string;
    contentEditable: boolean;
    drag: boolean;
    resize: boolean;
    color: string;
}

export interface SnippetData {
    id: number;
    audioFileId: number;
    regionId: string;
    startTime: number;
    endTime: number;
    isActive: boolean;
    name: string;
}

export class AuditioBackendManager {
    private baseUrl: string;

    constructor() {
        // Use configuration-based URL instead of hardcoded
        this.baseUrl = config.apiBaseUrl;
        console.log(`AuditioBackendManager initialized with API URL: ${this.baseUrl} (${config.environment} mode)`);
        this.initBackendAPI();
    }

    private async initBackendAPI() {
        try {
            await this.testCors();
            console.log('✅ Backend API connection successful');
        } catch (error) {
            console.error('❌ Backend API connection failed:', error);
            throw error;
        }
    }

    public getBaseUrl(): string {
        return this.baseUrl;
    }

    // Note: Logging is now handled by the global LoggingContext
    // The addLog calls below will be updated to use the context

    /**
     * Ensures all regions are visible by checking their pixel width and adjusting zoom if necessary
     * @param wavesurfer - The WaveSurfer instance
     * @param currentZoom - The current zoom level
     * @param minRegionWidth - Minimum pixel width for a region to be considered visible (default: 30)
     * @returns The new zoom level if adjusted, or null if no adjustment was needed
     */
    public ensureRegionVisibility(wavesurfer: any, currentZoom: number, minRegionWidth: number = 30): number | null {
        try {
            const regionsPlugin = wavesurfer.getActivePlugins()[1] as RegionsPlugin;
            const allRegions = regionsPlugin.getRegions();
            
            // Check if any regions are too small to be visible
            let needsZoomAdjustment = false;
            let minRequiredZoom = 0;
            
            for (const region of Object.values(allRegions)) {
                const regionDuration = region.end - region.start;
                const regionPixels = regionDuration * currentZoom;
                
                if (regionPixels < minRegionWidth) {
                    needsZoomAdjustment = true;
                    const requiredZoom = minRegionWidth / regionDuration;
                    minRequiredZoom = Math.max(minRequiredZoom, requiredZoom);
                }
            }
            
            if (needsZoomAdjustment) {
                console.log('🔍 Region visibility check: adjusting zoom for better visibility');
                
                // Apply the zoom adjustment
                const newZoom = Math.max(minRequiredZoom, currentZoom);
                wavesurfer.zoom(newZoom);
                
                console.log('🔍 Zoom adjusted from', currentZoom, 'to', newZoom, 'for region visibility');
                return newZoom;
            }
            
            return null; // No adjustment needed
        } catch (error) {
            console.warn('Error in region visibility check:', error);
            return null;
        }
    }

    public async updateActiveFile(fileId: number): Promise<number> {
        try {
            const response = await fetch(`${this.baseUrl}/updateActiveFile/${fileId}`, {
                method: 'PUT'
            });
            if (!response.ok) {
                throw new Error(`Error updating active file: ${response.statusText}`);
            }
            const result = await response.json();
            
            return result.id;
        } catch (error) {
            console.error('updateActiveFile error:', error);
            throw error;
        }
    }

    public async deleteFile(fileId: number, idToken?: string): Promise<void> {
        try {
            // Delete the file using the DELETE endpoint (backend handles snippet cleanup)
            console.log(`🗑️ Deleting file ${fileId}...`);
            const deleteFileResponse = await fetch(`${this.baseUrl}/files/${fileId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
                }
            });
            if (!deleteFileResponse.ok) {
                throw new Error(`Error deleting file: ${deleteFileResponse.statusText}`);
            }

            console.log(`✅ File ${fileId} deleted successfully (snippets cleaned up by backend)`);
        } catch (error) {
            console.error('deleteFile error:', error);
            throw error;
        }
    }

    public async updateFileName(fileId: number, fileName: string, idToken?: string): Promise<AudioFile> {
        try {
            console.log(`✏️ Updating file ${fileId} name to: ${fileName}`);
            const response = await fetch(`${this.baseUrl}/files/${fileId}/name`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
                },
                body: JSON.stringify({ fileName }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update file name');
            }

            const updatedFile = await response.json();
            console.log(`✅ File ${fileId} name updated successfully to: ${fileName}`);
            return updatedFile;
        } catch (error) {
            console.error(`❌ Error updating file name for ${fileId}:`, error);
            throw error;
        }
    }

    public async uploadFile(
        file: File,
        audioData: string | ArrayBuffer | null | undefined,
        setMessage: React.Dispatch<React.SetStateAction<string>>,
        idToken?: string
    ): Promise<AudioFile> {
        try {
            logMessage('=== FRONTEND UPLOAD STARTED ===');
            logMessage('📁 Original file details', {
                name: file.name,
                size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
                type: file.type,
                lastModified: new Date(file.lastModified).toISOString()
            });
            logMessage('🌐 Backend URL', this.baseUrl);
            
            const convertedFile = await this.convertToMp3(file, setMessage);
            logMessage('🔄 File conversion completed', {
                originalName: file.name,
                convertedName: convertedFile.name,
                originalSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
                convertedSize: `${(convertedFile.size / 1024 / 1024).toFixed(2)} MB`,
                convertedType: convertedFile.type,
                sizeDifference: `${((file.size - convertedFile.size) / 1024 / 1024).toFixed(2)} MB`,
                compressionRatio: `${((1 - convertedFile.size / file.size) * 100).toFixed(1)}%`
            });
            
            // Check if converted file is too large (over 32MB)
            const maxFileSizeMB = 32;
            const convertedFileSizeMB = convertedFile.size / (1024 * 1024);
            
            if (convertedFileSizeMB > maxFileSizeMB) {
                const errorMessage = `File is too large after conversion. Converted size: ${convertedFileSizeMB.toFixed(2)} MB. Maximum allowed: ${maxFileSizeMB} MB.`;
                logMessage('❌ File size validation failed', {
                    convertedSize: `${convertedFileSizeMB.toFixed(2)} MB`,
                    maxAllowed: `${maxFileSizeMB} MB`,
                    error: errorMessage
                });
                throw new Error(errorMessage);
            }
            
            logMessage('✅ File size validation passed', {
                convertedSize: `${convertedFileSizeMB.toFixed(2)} MB`,
                maxAllowed: `${maxFileSizeMB} MB`
            });
            
            const formData = new FormData();
            formData.append('file', convertedFile);

            // Don't send audioData as it's redundant - the file is already being sent
            logMessage('📊 File upload only (no redundant audio data)');
            
            logMessage('🚀 Making upload request to backend');
            logMessage('📍 Upload endpoint', `${this.baseUrl}/upload`);
            logMessage('📦 FormData details', {
                hasFile: formData.has('file'),
                hasData: formData.has('data'),
                fileSize: convertedFile.size,
                fileSizeMB: `${(convertedFile.size / 1024 / 1024).toFixed(2)} MB`,
                fileType: convertedFile.type,
                fileName: convertedFile.name
            });
            
            logMessage('🌐 Request details', {
                method: 'POST',
                fileSize: convertedFile.size,
                fileSizeMB: `${(convertedFile.size / 1024 / 1024).toFixed(2)} MB`,
                contentType: 'multipart/form-data',
                backendUrl: this.baseUrl,
                environment: this.baseUrl.includes('localhost') ? 'development' : 'production',
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString(),
                online: navigator.onLine,
                connectionType: (navigator as any).connection?.effectiveType || 'unknown'
            });
            
            // Create AbortController for timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => {
                controller.abort();
                logMessage('⏰ Upload timeout after 5 minutes');
            }, 300000); // 5 minutes
            
            // Add unhandled rejection handler for this specific upload
            const unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
                logMessage('❌ Unhandled promise rejection detected', {
                    reason: event.reason,
                    type: typeof event.reason
                });
            };
            window.addEventListener('unhandledrejection', unhandledRejectionHandler);
            
            // Test basic connectivity first
            try {
                logMessage('🔍 Testing basic connectivity...');
                const testResponse = await fetch(`${this.baseUrl}/version`, { 
                    method: 'GET',
                    signal: controller.signal 
                });
                logMessage('✅ Connectivity test passed', { status: testResponse.status });
            } catch (testError) {
                logMessage('❌ Connectivity test failed', { 
                    error: testError instanceof Error ? testError.message : String(testError) 
                });
            }
            
            let response: Response;
            
            try {
                logMessage('🚀 Starting fetch request...');
                logMessage('⏱️ Upload start time', new Date().toISOString());
                
                response = await fetch(`${this.baseUrl}/upload`, {
                    method: 'POST',
                    mode: 'cors',
                    headers: idToken ? {
                        'Authorization': `Bearer ${idToken}`
                    } : {},
                    body: formData,
                    signal: controller.signal
                });
                
                clearTimeout(timeoutId);
                logMessage('⏱️ Upload completed at', new Date().toISOString());
                
                logMessage('📡 Upload response received', {
                    status: response.status,
                    statusText: response.statusText,
                    ok: response.ok,
                    headers: Object.fromEntries(response.headers.entries())
                });
            } catch (fetchError) {
                clearTimeout(timeoutId);
                logMessage('⏱️ Upload failed at', new Date().toISOString());
                
                if (fetchError instanceof Error) {
                    if (fetchError.name === 'AbortError') {
                        logMessage('❌ Upload aborted due to timeout');
                    } else {
                        logMessage('❌ Fetch error details', {
                            name: fetchError.name,
                            message: fetchError.message,
                            stack: fetchError.stack,
                            errorType: fetchError.constructor.name
                        });
                        
                        // Try to get more context about the error
                        if ('cause' in fetchError) {
                            logMessage('❌ Fetch error cause', (fetchError as any).cause);
                        }
                    }
                } else {
                    logMessage('❌ Unknown fetch error', {
                        type: typeof fetchError,
                        value: fetchError
                    });
                }
                throw fetchError;
            } finally {
                // Clean up resources
                clearTimeout(timeoutId);
                window.removeEventListener('unhandledrejection', unhandledRejectionHandler);
            }
                
            if (!response.ok) {
                console.error('❌ Upload failed with status:', response.status);
                throw new Error(`Upload API error: ${response.statusText}`);
            }
                
            const result = await response.json();
            
            logMessage('🚨🚨 UPLOAD RESPONSE CAPTURED 🚨🚨');
            logMessage('🚨🚨 FULL RESPONSE OBJECT', JSON.stringify(result, null, 2));
            logMessage('🚨🚨 CLOUD FILENAME', result.cloudFileName);
            logMessage('🚨🚨 LOCAL FILE PATH', result.localFilePath);
            
            logMessage('✅ Upload successful, backend response', {
                id: result.id,
                fileName: result.fileName,
                uploadTime: result.uploadTime,
                dataLength: result.data ? `${(result.data.length / 1024 / 1024).toFixed(2)} MB` : '0 MB',
                cloudFileName: result.cloudFileName || 'null (local storage)',
                localFilePath: result.localFilePath || 'null (local storage)',
                hasCloudFileName: !!result.cloudFileName,
                hasLocalFilePath: !!result.localFilePath
            });
            
            const newFile: AudioFile = {
                id: result.id,
                data: result.data,
                fileName: result.fileName,
                uploadTime: new Date(result.uploadTime),
                cloudFileName: result.cloudFileName,
                localFilePath: result.localFilePath,
                snippets: []
            };

            // Update current file if this is active
            // if (newFile.isActive) { // This line is removed
            //     this.currentAudioFile = newFile;
            //     console.log('🎯 File set as active audio file');
            // }

            logMessage('=== FRONTEND UPLOAD COMPLETE ===');
            return newFile;
        } catch (error) {
            logMessage('❌ Upload error', {
                error: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined
            });
            console.error('uploadFile error:', error);
            throw error;
        }
    }

    private async convertToMp3(file: File, setMessage: React.Dispatch<React.SetStateAction<string>>): Promise<File> {
        let inputFileName = file.name;
        let outputFileName = file.name;

        // check if file is an mp3 file
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const isMp3 = fileExtension === 'mp3';

        if (isMp3) {
            return file;
        }

        setMessage('Converting file to .mp3...');

        // Load FFmpeg if needed
        const ffmpeg = new FFmpeg();
        await ffmpeg.load();

        // Convert the file to MP3 using FFmpeg.js
        outputFileName = file.name.replace(/\.[^/.]+$/, ".mp3");

        await ffmpeg.writeFile(inputFileName, new Uint8Array(await file.arrayBuffer()));

        await ffmpeg.exec(['-i', file.name,
            '-codec:a', 'libmp3lame', outputFileName]);

        const mp3Data = await ffmpeg.readFile(outputFileName);
        setMessage('File converted successfully!');

        // Create a Blob from the MP3 data and create a new File object
        const mp3Blob = new Blob([mp3Data], { type: 'audio/mp3' });
        const convertedFile = new File([mp3Blob], outputFileName, { type: 'audio/mp3' });

        return convertedFile;
    }

    public async fetchAllFiles(): Promise<AudioFile[]> {
        try {
            const response = await fetch(`${this.baseUrl}/files`);
            if (!response.ok) {
                throw new Error(`Error fetching files: ${response.statusText}`);
            }

            const files = await response.json() as Array<{
                id: number;
                data: string;
                fileName: string;
                uploadTime: number;
                isActive: boolean;
                snippets: SnippetData[];
            }>; 

            // Handle case where backend returns empty object instead of empty array
            if (!Array.isArray(files)) {
                if (typeof files === 'object' && files !== null && Object.keys(files).length === 0) {
                    // Backend returned empty object, treat as empty array
                    console.log('Backend returned empty object, treating as empty array');
                    return [];
                }
                console.warn('Backend returned non-array response for files:', files);
                return [];
            }

            return files.map(f => ({
                id: f.id ?? '',
                data: f.data ?? '',
                fileName: f.fileName ?? '',
                uploadTime: new Date(f.uploadTime ?? 0),
                snippets: f.snippets ?? []
            }));
        } catch (error) {
            console.error('fetchAllFiles error:', error);
            throw error;
        }
    }

    public async fetchUserFiles(idToken: string): Promise<AudioFile[]> {
        try {
            console.log('🔍 AuditioBackend.fetchUserFiles - Starting request');
            console.log('🔍 AuditioBackend.fetchUserFiles - Base URL:', this.baseUrl);
            console.log('🔍 AuditioBackend.fetchUserFiles - Token present:', !!idToken);
            console.log('🔍 AuditioBackend.fetchUserFiles - Token length:', idToken ? idToken.length : 0);
            console.log('🔍 AuditioBackend.fetchUserFiles - Token preview:', idToken ? `${idToken.substring(0, 20)}...` : 'none');
            
            const url = `${this.baseUrl}/files/my`;
            console.log('🔍 AuditioBackend.fetchUserFiles - Full URL:', url);
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                },
                mode: 'cors',
                credentials: 'include'
            });

            console.log('🔍 AuditioBackend.fetchUserFiles - Response status:', response.status);
            console.log('🔍 AuditioBackend.fetchUserFiles - Response ok:', response.ok);
            console.log('🔍 AuditioBackend.fetchUserFiles - Response headers:', Object.fromEntries(response.headers.entries()));

            if (!response.ok) {
                if (response.status === 401) {
                    // Try to get detailed error message from response
                    let errorMessage = 'Authentication required';
                    try {
                        const errorData = await response.json();
                        console.error('❌ AuditioBackend.fetchUserFiles - Error response:', errorData);
                        if (errorData.error) {
                            errorMessage = errorData.error;
                        }
                        if (errorData.message) {
                            errorMessage += `: ${errorData.message}`;
                        }
                    } catch (e) {
                        console.error('❌ AuditioBackend.fetchUserFiles - Could not parse error response:', e);
                    }
                    throw new Error(errorMessage);
                }
                // Try to get error message from response
                let errorMessage = `Error fetching user files: ${response.statusText}`;
                try {
                    const errorData = await response.json();
                    console.error('❌ AuditioBackend.fetchUserFiles - Error response:', errorData);
                    if (errorData.error) {
                        errorMessage = errorData.error;
                    }
                } catch (e) {
                    console.error('❌ AuditioBackend.fetchUserFiles - Could not parse error response:', e);
                }
                throw new Error(errorMessage);
            }

            const files = await response.json() as Array<{
                id: number;
                data: string;
                fileName: string;
                uploadTime: number;
                youtubeUrl?: string;
                driveFileId?: string;
                driveFileUrl?: string;
                ownerDisplayName?: string;
                ownerId?: number;
                snippets: SnippetData[];
            }>; 

            // Handle case where backend returns empty object instead of empty array
            if (!Array.isArray(files)) {
                if (typeof files === 'object' && files !== null && Object.keys(files).length === 0) {
                    // Backend returned empty object, treat as empty array
                    console.log('Backend returned empty object, treating as empty array');
                    return [];
                }
                console.warn('Backend returned non-array response for user files:', files);
                return [];
            }

            const mappedFiles = files.map(f => {
                const mapped = {
                    id: f.id ?? '',
                    data: f.data ?? '',
                    fileName: f.fileName ?? '',
                    uploadTime: new Date(f.uploadTime ?? 0),
                    youtubeUrl: f.youtubeUrl,
                    driveFileId: f.driveFileId,
                    driveFileUrl: f.driveFileUrl,
                    ownerDisplayName: f.ownerDisplayName,
                    ownerId: f.ownerId,
                    snippets: f.snippets ?? []
                };
                // Debug: Log file mapping
                console.log('🔍 AuditioBackend.fetchUserFiles - Mapped file:', {
                    id: mapped.id,
                    fileName: mapped.fileName,
                    hasYoutubeUrl: !!mapped.youtubeUrl,
                    hasDriveFileId: !!mapped.driveFileId,
                    hasDriveFileUrl: !!mapped.driveFileUrl,
                    originalYoutubeUrl: f.youtubeUrl,
                    originalDriveFileId: f.driveFileId,
                    originalDriveFileUrl: f.driveFileUrl
                });
                return mapped;
            });
            return mappedFiles;
        } catch (error) {
            console.error('fetchUserFiles error:', error);
            throw error;
        }
    }

    public async deleteAllFiles(idToken?: string): Promise<void> {
        try {
            // Use the dedicated deleteAllFiles endpoint
            const response = await fetch(`${this.baseUrl}/deleteAllFiles`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
                }
            });
            
            if (!response.ok) {
                throw new Error(`Error deleting all files: ${response.statusText}`);
            }

            console.log('All files deleted successfully');
        } catch (error) {
            console.error('deleteAllFiles error:', error);
            throw error;
        }
    }

    public async deleteOldFiles() {
        try {
            const allFiles = await this.fetchAllFiles();
            const now = (new Date).getTime();
            const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    
            // Loop over all the files and delete those older than 7 days
            for (const file of allFiles) {
                const fileAge = now - file.uploadTime.getTime();
                if (fileAge > sevenDaysInMs) {
                    console.log("file is older than 7 days. current time: " + now + ", file upload time: " + file.uploadTime.getTime());
                    try {
                        await this.deleteFile(file.id);
                        console.log(`File ${file.id} deleted successfully`);
                    } catch (error) {
                        console.error(`Error deleting file ${file.id}:`, error);
                    }
                }
            }
        } catch (error) {
            console.error('deleteOldFiles error:', error);
        }
    }

    // Check if a specific file should be deleted and delete it if so
    public async checkAndDeleteFileIfExpired(fileId: number): Promise<boolean> {
        try {
            const file = await this.getAudioFileById(fileId);
            if (!file) {
                console.log(`File ${fileId} not found, skipping deletion check`);
                return false;
            }

            const now = (new Date).getTime();
            const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
            const fileAge = now - file.uploadTime.getTime();

            if (fileAge > sevenDaysInMs) {
                console.log(`File ${fileId} is expired (${Math.floor(fileAge / (24 * 60 * 60 * 1000))} days old), deleting...`);
                try {
                    await this.deleteFile(fileId);
                    console.log(`Expired file ${fileId} deleted successfully`);
                    return true; // File was deleted
                } catch (error) {
                    console.error(`Error deleting expired file ${fileId}:`, error);
                    return false;
                }
            }

            return false; // File was not deleted
        } catch (error) {
            console.error(`Error checking file ${fileId} for expiration:`, error);
            return false;
        }
    }

    // Delete old files for user-uploaded files only (background process)
    public async deleteOldUserFiles(): Promise<void> {
        try {
            console.log('🧹 Starting background deletion of old user files...');
            const allFiles = await this.fetchAllFiles();
            const now = (new Date).getTime();
            const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
            
            // Filter to only user-uploaded files (not shared files)
            const userFiles = allFiles.filter(file => {
                // Check if this is a user-uploaded file by looking at session data
                const recentFiles = SessionManager.getRecentFiles();
                const sessionFile = recentFiles.find(f => f.id === file.id);
                return sessionFile?.isUserUploaded === true;
            });

            console.log(`🔍 Found ${userFiles.length} user-uploaded files to check for deletion`);

            // Process deletions asynchronously without blocking
            const deletionPromises = userFiles.map(async (file) => {
                const fileAge = now - file.uploadTime.getTime();
                if (fileAge > sevenDaysInMs) {
                    console.log(`🗑️ User file ${file.id} (${file.fileName}) is ${Math.floor(fileAge / (24 * 60 * 60 * 1000))} days old, scheduling deletion`);
                    try {
                        await this.deleteFile(file.id);
                        console.log(`✅ User file ${file.id} deleted successfully`);
                        return { deleted: true, fileId: file.id };
                    } catch (error) {
                        console.error(`❌ Error deleting user file ${file.id}:`, error);
                        return { deleted: false, fileId: file.id, error };
                    }
                }
                return { deleted: false, fileId: file.id };
            });

            // Wait for all deletions to complete
            const results = await Promise.allSettled(deletionPromises);
            const deletedCount = results.filter(result => 
                result.status === 'fulfilled' && result.value.deleted
            ).length;

            console.log(`🧹 Background deletion complete: ${deletedCount} files deleted`);
        } catch (error) {
            console.error('❌ Error in background deletion process:', error);
        }
    }

    public async loadSnippetsForCurrentAudioFile(wavesurfer: any, regions: any, regionRef: any, setStartMarker: any, setEndMarker: any, currentZoom: number = 20, onSnippetsLoaded?: () => void, isOwner: boolean = true) {
        try {
            console.log('🔍 loadSnippetsForCurrentAudioFile called');
            
            // Get the current audio file ID from the URL or state
            const path = window.location.pathname;
            const audioFileMatch = path.match(/^\/(\d+)$/);
            
            if (!audioFileMatch) {
                console.error('❌ No audio file ID found in URL');
                return;
            }
            
            const audioFileId = parseInt(audioFileMatch[1]);
            console.log('🔍 Loading snippets for audio file ID:', audioFileId);
            
            // Get snippets for this specific audio file
            const response = await fetch(`${this.baseUrl}/snippets/${audioFileId}`);
            
            if (!response.ok) {
                console.error('❌ Error loading snippets:', response.statusText);
                return;
            }
            
            const snippets = await response.json();
            console.log('🔍 Loaded snippets:', snippets);
            
            // Get wavesurfer region plugin
            const regionsPlugin = wavesurfer.getActivePlugins()[1] as RegionsPlugin;
            regionsPlugin.clearRegions();
            regions.current = {}; 

            // Clear any existing markers from state
            setStartMarker(null);
            setEndMarker(null);

            // Create all regions - let WaveSurfer's event system handle everything
            snippets.forEach((snippet: any) => {
                const startTime = Number(snippet.startTime);
                const endTime = Number(snippet.endTime);

                console.log('🔍 Creating region for snippet:', snippet.name, 'with ID:', snippet.regionId, 'isOwner:', isOwner);

                // Just call addRegion - the region-created event will handle everything else
                const regionConfig = {
                    id: snippet.regionId,
                    start: startTime,
                    end: endTime,
                    content: snippet.name,
                    contentEditable: isOwner,  // Only allow content editing for owners
                    drag: isOwner,  // Only allow dragging for owners
                    resize: isOwner,  // Only allow resizing for owners
                    color: 'rgba(252, 185, 170, 0.18)',
                };
                console.log('🔍 AuditioBackend - Creating region with config:', regionConfig);
                const createdRegion = regionsPlugin.addRegion(regionConfig);
                console.log('🔍 AuditioBackend - Region created, drag:', createdRegion?.drag, 'resize:', createdRegion?.resize);
            });

            await this.updateActiveSnippetFromDB(regionRef, regions, audioFileId);
            
            // Call the callback to notify that snippets are loaded and active state is restored
            if (onSnippetsLoaded) {
                onSnippetsLoaded();
            }
            
            // Force a re-render to ensure regions are visible even when zoomed out
            console.log('🔍 Forcing re-render to ensure regions are visible');
            
            // Use the new helper method to ensure region visibility
            setTimeout(() => {
                const newZoom = this.ensureRegionVisibility(wavesurfer, currentZoom, 30); // Pass current zoom and min width
                if (newZoom === null) {
                    // If no zoom adjustment was needed, still do a standard force re-render
                    // Since getZoom() doesn't exist, we'll just do a simple zoom adjustment
                    wavesurfer.zoom(currentZoom + 0.1); // Small zoom increase
                    setTimeout(() => {
                        wavesurfer.zoom(currentZoom); // Back to normal
                    }, 50);
                }
            }, 100);
        } catch (error) {
            console.error('❌ Error loading snippets for current audio file:', error);
        }
    }

    /**
     * Deletes a snippet by regionId and audioFileId, removes the region from the UI, and returns the updated snippets array.
     * @param regionId - The region ID to delete
     * @param regions - The regions object from wavesurfer
     * @param regionRef - The regionRef object
     * @param audioFileId - The audio file ID
     * @returns The updated snippets array after deletion
     */
    public async deleteSnippet(regionId: string, regions: any, regionRef: any, audioFileId: number): Promise<SnippetData[]> {
        try {
            const response = await fetch(`${this.baseUrl}/snippets`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    audioFileId: audioFileId,
                    regionId,
                }),
            });

            if (!response.ok) {
                throw new Error(`Error deleting snippet: ${response.statusText}`);
            }

            // Remove region from UI
            if (regions.current[regionId]) {
                if (regionRef.current?.id === regionId) {
                    regionRef.current = null;
                }
                regions.current[regionId].remove();
                delete regions.current[regionId];
            }

            // Fetch updated snippets from the backend
            const snippetsResponse = await fetch(`${this.baseUrl}/snippets/${audioFileId}`);
            if (!snippetsResponse.ok) {
                console.error('Error fetching updated snippets after deletion:', snippetsResponse.statusText);
                return [];
            }
            
            const updatedSnippets = await snippetsResponse.json();
            console.log('🔍 Updated snippets after deletion:', updatedSnippets);
            
            return updatedSnippets;
        } catch (error) {
            console.error('deleteSnippet error:', error);
            throw error;
        }
    }

    public async updateActiveRegion(regionRef: any, newRegionId: string, regions: any, audioFileId: number) {
        try {
            // Update Old Region to be inactive
            if (regionRef.current && regions.current[regionRef.current.id]) {
                // Update UI
                regions.current[regionRef.current.id].setOptions({
                    color: 'rgba(252, 185, 170, 0.18)'
                });

                // Set old region as inactive in backend
                const inactiveUrl = `${this.baseUrl}/snippets/${audioFileId}/${regionRef.current.id}/inactive`;
                
                await fetch(inactiveUrl, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
            } else {
                // If no region is currently active, find and deactivate any active snippets
                try {
                    const snippetsResponse = await fetch(`${this.baseUrl}/snippets/${audioFileId}`);
                    if (snippetsResponse.ok) {
                        const snippets = await snippetsResponse.json();
                        const activeSnippet = snippets.find((snippet: SnippetData) => snippet.isActive);
                        
                        if (activeSnippet && activeSnippet.regionId !== newRegionId) {
                            // Set it to inactive
                            const inactiveUrl = `${this.baseUrl}/snippets/${audioFileId}/${activeSnippet.regionId}/inactive`;
                            
                            const inactiveResponse = await fetch(inactiveUrl, {
                                method: 'PUT',
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            });

                            if (!inactiveResponse.ok) {
                                throw new Error(`Error setting snippet inactive: ${inactiveResponse.statusText}`);
                            }

                            // Update UI to show inactive state
                            if (regions.current[activeSnippet.regionId]) {
                                regions.current[activeSnippet.regionId].setOptions({
                                    color: 'rgba(252, 185, 170, 0.18)'
                                });
                            }
                        }
                    }
                } catch (error) {
                    console.warn('Could not fetch snippets for deactivation:', error);
                }
            }

            // Update the active snippet in the DB
            const response = await fetch(`${this.baseUrl}/snippets/${audioFileId}/${newRegionId}/active`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Error updating active region: ${response.statusText}`);
            }

            // Update new region to be active
            if (newRegionId && regions.current && regions.current[newRegionId]) {
                const newRegion = regions.current[newRegionId];
                if (newRegion && typeof newRegion.setOptions === 'function') {
                    regionRef.current = newRegion;
                    newRegion.setOptions({
                        color: 'rgba(252, 185, 170, 0.28)'
                    });
                }
            }
        } catch (error) {
            console.error('updateActiveRegion error:', error);
            throw error;
        }
    }

    // NOTE: DO NOT set region to active within this function. Please do it outside. 
    // Since this function is asynchronous and can be called on multiple snippets/regions in quick succession, we need to wait until this update is complete.
public async addOrUpdateSnippetFromRegion(region: any, audioFileId: number) {
        console.log('🔍 addOrUpdateSnippetFromRegion called with region:', region.id);

        if (!region || !region.id || region.start === undefined || region.end === undefined) {
            console.error('❌ Invalid region object:', region);
            throw new Error('Invalid region object - missing required properties');
        }

        // Validate audioFileId
        if (!audioFileId || audioFileId <= 0) {
            console.error('❌ Invalid audioFileId:', audioFileId);
            throw new Error('Invalid audioFileId');
        }

        // Extract name from the region's element or content
        let regionName = "New Region";
        try {
            if (region.content) {
                if (typeof region.content === 'string') {
                    regionName = region.content;
                } else if (region.content.innerText) {
                    regionName = region.content.innerText;
                } else if (region.content.textContent) {
                    regionName = region.content.textContent;
                }
            }
        } catch (error) {
            console.warn('Error extracting region name, using default:', error);
        }

        // Sanitize region name
        if (!regionName || regionName.trim() === '') {
            regionName = "New Region";
        }

        // Validate region data
        const startTime = Number(region.start);
        const endTime = Number(region.end);
        
        if (isNaN(startTime) || isNaN(endTime)) {
            console.error('❌ Invalid region times:', { start: region.start, end: region.end });
            throw new Error('Invalid region times - must be valid numbers');
        }

        // Allow markers (same start and end time) but not invalid ranges
        if (startTime > endTime) {
            console.error('❌ Invalid region: start time must be less than or equal to end time', {
                start: startTime,
                end: endTime,
                regionId: region.id
            });
            throw new Error('Invalid region: start time must be less than or equal to end time');
        }

        if (startTime < 0 || endTime < 0) {
            console.error('❌ Invalid region: times must be positive', {
                start: startTime,
                end: endTime,
                regionId: region.id
            });
            throw new Error('Invalid region: times must be positive');
        }

        // Get existing snippets to preserve active state
        let existingSnippet = null;
        try {
            const existingSnippetsResponse = await fetch(`${this.baseUrl}/snippets/${audioFileId}`);
            if (existingSnippetsResponse.ok) {
                const existingSnippets = await existingSnippetsResponse.json();
                existingSnippet = existingSnippets.find((s: any) => s.regionId === region.id);
            }
        } catch (error) {
            console.log('No existing snippets found for this region');
        }

        const snippet: SnippetData = {
            id: 0, // This will be set by the backend
            audioFileId: audioFileId,
            regionId: String(region.id), // Ensure regionId is a string
            startTime: startTime,
            endTime: endTime,
            isActive: existingSnippet ? existingSnippet.isActive : false,
            name: regionName.trim(), // Trim whitespace
        };

        console.log('🔍 Snippet data being sent to backend:', {
            id: snippet.id,
            audioFileId: snippet.audioFileId,
            regionId: snippet.regionId,
            startTime: snippet.startTime,
            endTime: snippet.endTime,
            name: snippet.name,
            isActive: snippet.isActive
        });

        try {
            const requestBody = { snippet };
            console.log('🔍 Request body being sent:', JSON.stringify(requestBody, null, 2));
            
            // Test backend connectivity first
            try {
                const testResponse = await fetch(`${this.baseUrl}/health`, { method: 'GET' });
                console.log('🔍 Backend health check status:', testResponse.status);
            } catch (healthError) {
                console.warn('⚠️ Backend health check failed:', healthError);
            }
            
            const response = await fetch(`${this.baseUrl}/snippets`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            console.log('🔍 Response status:', response.status);
            console.log('🔍 Response headers:', Object.fromEntries(response.headers.entries()));

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ Server error response:', {
                    status: response.status,
                    statusText: response.statusText,
                    errorText: errorText
                });
                throw new Error(`Error adding/updating snippet: ${response.status} - ${errorText}`);
            }

            const result = await response.json();
            console.log('✅ Snippet added/updated successfully:', result);
            return result;
        } catch (error) {
            console.error('❌ Error adding/updating snippet:', error);
            console.error('❌ Error details:', {
                message: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined,
                region: {
                    id: region.id,
                    start: region.start,
                    end: region.end,
                    content: region.content
                },
                audioFileId: audioFileId,
                baseUrl: this.baseUrl
            });
            throw error;
        }
    }

    public async updateSnippet(snippet: SnippetData) {
        try {
            const response = await fetch(`${this.baseUrl}/snippets`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ snippet }),
            });

            if (!response.ok) {
                throw new Error(`Error updating snippet: ${response.statusText}`);
            }
        } catch (error) {
            console.error('updateSnippet error:', error);
            throw error;
        }
    }

    public async trimSnippet(regionId: string, regions: any, audioFileId: number): Promise<Blob> {
        console.log('🔧 BACKEND MANAGER: trimSnippet called', {
            regionId: regionId,
            audioFileId: audioFileId,
            timestamp: new Date().toISOString()
        });
        
        const regionToTrim = regions.current[regionId];
        
        if (!regionToTrim) {
            console.error('🔧 BACKEND MANAGER: Region not found', { regionId });
            throw new Error(`Region ${regionId} not found`);
        }
        
        console.log('🔧 BACKEND MANAGER: Region found', {
            regionId: regionId,
            start: regionToTrim.start,
            end: regionToTrim.end,
            content: regionToTrim.content
        });
        
        // Get the snippet name more robustly
        let outputFileName = 'Snippet';
        if (regionToTrim.content) {
            if (typeof regionToTrim.content === 'string') {
                outputFileName = regionToTrim.content;
            } else if (regionToTrim.content.innerText) {
                outputFileName = regionToTrim.content.innerText;
            } else if (regionToTrim.content.textContent) {
                outputFileName = regionToTrim.content.textContent;
            }
        }

        console.log('🔧 BACKEND MANAGER: Prepared trim request', {
            audioFileId: audioFileId,
            start: regionToTrim.start,
            end: regionToTrim.end,
            output_name: outputFileName,
            baseUrl: this.baseUrl
        });

        try {
            console.log('🔧 BACKEND MANAGER: Making fetch request to /trim endpoint...');
            const response = await fetch(`${this.baseUrl}/trim`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    audioFileId: audioFileId,
                    start: regionToTrim.start,
                    end: regionToTrim.end,
                    output_name: outputFileName,
                }),
            });

            console.log('🔧 BACKEND MANAGER: Received response from /trim', {
                status: response.status,
                statusText: response.statusText,
                ok: response.ok
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('🔧 BACKEND MANAGER: Trim request failed:', response.status, errorText);
                throw new Error(`Error trimming audio: ${response.status} - ${errorText}`);
            }

            console.log('🔧 BACKEND MANAGER: Parsing JSON response...');
            const result = await response.json();
            console.log('🔧 BACKEND MANAGER: Trim result received', result);
            
            console.log('🔧 BACKEND MANAGER: Making download request for trimmed file...');
            const trimmedFileResponse = await fetch(`${this.baseUrl}/download/${result.output_filename}`);
            console.log('🔧 BACKEND MANAGER: Download response received', {
                status: trimmedFileResponse.status,
                statusText: trimmedFileResponse.statusText,
                ok: trimmedFileResponse.ok
            });
            
            if (!trimmedFileResponse.ok) {
                const errorText = await trimmedFileResponse.text();
                console.error('🔧 BACKEND MANAGER: Download request failed:', trimmedFileResponse.status, errorText);
                throw new Error(`Error downloading trimmed file: ${trimmedFileResponse.status} - ${errorText}`);
            }

            console.log('🔧 BACKEND MANAGER: Converting response to blob...');
            const blob = await trimmedFileResponse.blob();
            console.log('🔧 BACKEND MANAGER: Blob created successfully', {
                size: blob.size,
                type: blob.type
            });
            
            return blob;
        } catch (error) {
            console.error('🔧 BACKEND MANAGER: Error in trimSnippet:', error);
            throw error;
        }
    }

    public setCurrentAudioFile(audioFile: AudioFile | null) {
        // This method is no longer needed as we don't track current audio file
        console.log('🔍 setCurrentAudioFile called but no longer needed');
    }

    public async updateActiveSnippetFromDB(regionRef: any, regions: any, audioFileId: number) {
        if (!regions || !regions.current) {
            console.log("No regions object provided");
            return;
        }

        try {
            // Get snippets for this specific audio file from the backend
            const response = await fetch(`${this.baseUrl}/snippets/${audioFileId}`);
            
            if (!response.ok) {
                console.error('Error fetching snippets for active status update:', response.statusText);
                return;
            }
            
            const snippets = await response.json();
            console.log('🔍 Fetched snippets for active status update:', snippets);

            // Find the active snippet first
            const activeSnippet = snippets?.find((s: SnippetData) => s.isActive);
            console.log('🔍 Active snippet found:', activeSnippet);

            // Update each region's color based on its snippet's active status
            Object.values(regions.current).forEach((region: any) => {
                if (!region || typeof region.setOptions !== 'function') {
                    console.warn('Invalid region object found:', region);
                    return;
                }

                const matchingSnippet = snippets?.find((s: SnippetData) => s.regionId === region.id);
                if (matchingSnippet && region.id === matchingSnippet.regionId) {
                    try {
                        regions.current[region.id].setOptions({
                            color: matchingSnippet.isActive ? 'rgba(252, 185, 170, 0.28)' : 'rgba(252, 185, 170, 0.18)'
                        });
                    } catch (err) {
                        console.warn(`Failed to update region ${region.id} color:`, err);
                    }
                } else {
                    regions.current[region.id].setOptions({
                        color: 'rgba(252, 185, 170, 0.18)'
                    });
                }
            });

            // Set the regionRef to point to the active snippet if one exists
            if (activeSnippet && regions.current[activeSnippet.regionId]) {
                regionRef.current = regions.current[activeSnippet.regionId];
                console.log('🔍 Set regionRef.current to active snippet:', activeSnippet.regionId);
            } else {
                // No active snippet found, clear the regionRef
                regionRef.current = null;
                console.log('🔍 No active snippet found, cleared regionRef.current');
            }
        } catch (error) {
            console.error('Error updating active snippet from DB:', error);
            throw error; // Propagate the error
        }
    }

    /**
     * Checks if a region's properties match its corresponding snippet in the current audio file.
     * This is used to prevent unnecessary backend updates when a region hasn't actually changed.
     * 
     * @param region - The wavesurfer region to check
     * @param regions - The current regions map from wavesurfer
     * @param audioFileId - The ID of the audio file
     * @returns true if the region matches its corresponding snippet, false otherwise
     */
    public async areRegionAndSnippetEquivalent(region: any, regions: any, audioFileId: number): Promise<boolean> {
        console.log('🔍 areRegionAndSnippetEquivalent called for region:', region.id);
        
        if (!regions.current) {
            console.log('🔍 No regions.current, returning false');
            return false;
        }

        try {
            // Get all snippets for this audio file and find the matching one
            const response = await fetch(`${this.baseUrl}/snippets/${audioFileId}`);
            if (!response.ok) {
                console.log('🔍 Failed to fetch snippets, returning false');
                return false;
            }

            const existingSnippets = await response.json();
            console.log('🔍 Found existing snippets:', existingSnippets.length);
            
            const existingSnippet = existingSnippets.find((s: any) => s.regionId === region.id);
            console.log('🔍 Looking for region ID:', region.id, 'Found snippet:', existingSnippet ? 'yes' : 'no');

            if (!existingSnippet) {
                console.log('🔍 No existing snippet found, returning false');
                return false;
            }

            // Get the region name from either the element's text content or the content property
            const regionName = region.element ? region.element.textContent : 
                             (typeof region.content === 'string' ? region.content : 'New Snippet');

            const isEquivalent = existingSnippet.startTime === region.start &&
                   existingSnippet.endTime === region.end && 
                   existingSnippet.name === regionName &&
                   existingSnippet.isActive === (region.color === 'rgba(243, 160, 173, 0.3)');
                   
            console.log('🔍 Region equivalence check:', {
                regionId: region.id,
                existingStart: existingSnippet.startTime,
                regionStart: region.start,
                existingEnd: existingSnippet.endTime,
                regionEnd: region.end,
                existingName: existingSnippet.name,
                regionName: regionName,
                isEquivalent: isEquivalent
            });
            
            return isEquivalent;
        } catch (error) {
            console.error('Error checking region and snippet equivalence:', error);
            return false;
        }
    }

    public async testCors(): Promise<any> {
        try {
            const response = await fetch(`${this.baseUrl}/cors-test`);
            if (!response.ok) {
                throw new Error(`CORS test failed: ${response.statusText}`);
            }
            return await response.json();
        } catch (error) {
            console.error('CORS test error:', error);
            throw error;
        }
    }

    // Get audio file by ID
    public async getAudioFileById(id: number): Promise<AudioFile | null> {
        try {
            console.log('🔍 getAudioFileById - Requesting file with ID:', id);
            console.log('🔍 getAudioFileById - Backend URL:', `${this.baseUrl}/files/${id}`);
            
            const response = await fetch(`${this.baseUrl}/files/${id}`);
            
            console.log('🔍 getAudioFileById - Response status:', response.status);
            console.log('🔍 getAudioFileById - Response headers:', Object.fromEntries(response.headers.entries()));
            
            if (!response.ok) {
                if (response.status === 404) {
                    // File not found - this is expected for deleted files, don't log as error
                    return null;
                }
                const errorText = await response.text();
                console.error('🔍 getAudioFileById - Error response:', errorText);
                throw new Error(`Error getting audio file: ${response.statusText}`);
            }

            const result = await response.json();
            console.log('🔍 getAudioFileById - Success response:', {
                id: result.id,
                fileName: result.fileName,
                hasData: !!result.data,
                dataLength: result.data ? result.data.length : 0,
                uploadTime: result.uploadTime,
                snippetsCount: result.snippets ? result.snippets.length : 0,
                cloudFileName: result.cloudFileName || 'null',
                localFilePath: result.localFilePath || 'null',
                driveFileId: result.driveFileId || 'null',
                driveFileUrl: result.driveFileUrl || 'null',
                driveMimeType: result.driveMimeType || 'null',
                ownerDisplayName: result.ownerDisplayName || 'null',
                ownerId: result.ownerId,
                ownerIdType: typeof result.ownerId,
                hasOwnerId: result.ownerId != null,
                allKeys: Object.keys(result)
            });
            
            return {
                id: result.id,
                data: result.data,
                fileName: result.fileName,
                uploadTime: new Date(result.uploadTime),
                youtubeUrl: result.youtubeUrl,
                cloudFileName: result.cloudFileName,
                localFilePath: result.localFilePath,
                driveFileId: result.driveFileId,
                driveFileUrl: result.driveFileUrl,
                driveMimeType: result.driveMimeType,
                ownerDisplayName: result.ownerDisplayName,
                ownerId: result.ownerId,
                snippets: result.snippets
            };
        } catch (error) {
            console.error('🔍 getAudioFileById - Error:', error);
            throw error;
        }
    }

    /**
     * Download audio file and convert to data URL for WaveSurfer
     */
    public async downloadAudioFile(fileName: string): Promise<string> {
        try {
            console.log('📥 Downloading audio file:', fileName);
            const response = await fetch(`${this.baseUrl}/download/${fileName}`);
            
            if (!response.ok) {
                throw new Error(`Failed to download file: ${response.statusText}`);
            }
            
            const blob = await response.blob();
            console.log('📦 Blob received, size:', blob.size);
            
            // Convert blob to base64 more efficiently for large files
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const base64 = reader.result as string;
                    const dataUrl = `data:audio/mpeg;base64,${base64.split(',')[1]}`;
                    console.log('✅ Audio file downloaded successfully, data URL length:', dataUrl.length);
                    resolve(dataUrl);
                };
                reader.onerror = () => reject(new Error('Failed to read blob'));
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Error downloading audio file:', error);
            throw error;
        }
    }

    /**
     * Get audio data for a file, downloading from storage if needed
     */
    public async getAudioData(audioFile: AudioFile): Promise<string | null> {
        try {
            console.log('🔍 getAudioData called with:', {
                hasData: !!audioFile.data,
                dataLength: audioFile.data ? audioFile.data.length : 0,
                dataType: typeof audioFile.data,
                dataPreview: audioFile.data ? audioFile.data.substring(0, 50) + '...' : 'null',
                dataIsEmpty: audioFile.data === '',
                dataIsNull: audioFile.data === null,
                dataIsUndefined: audioFile.data === undefined,
                driveFileId: audioFile.driveFileId,
                youtubeUrl: audioFile.youtubeUrl
            });
            
            // If audio data exists in database, return it
            if (audioFile.data && audioFile.data.length > 0) {
                console.log('✅ Using audio data from database (length:', audioFile.data.length, ')');
                return audioFile.data;
            }

            // Check if this is a Google Drive file
            if (audioFile.driveFileId) {
                // #region agent log
                fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuditioBackend.ts:1434',message:'getAudioData: Drive file detected',data:{driveFileId:audioFile.driveFileId,hasDriveFileUrl:!!audioFile.driveFileUrl},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
                // #endregion
                
                console.log('📥 Loading audio data from Google Drive...', {
                    driveFileId: audioFile.driveFileId,
                    driveFileUrl: audioFile.driveFileUrl
                });
                
                try {
                    // Use backend endpoint to download (avoids CORS issues)
                    // Backend will try public download first, then authenticated if needed
                    const { GoogleDriveService } = await import('./services/GoogleDriveService');
                    const driveService = new GoogleDriveService();
                    
                    // #region agent log
                    fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuditioBackend.ts:1443',message:'getAudioData: About to call getAccessToken',data:{driveFileId:audioFile.driveFileId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
                    // #endregion
                    
                    // Get access token - required for private files
                    // If token retrieval fails, we need to prompt user to re-authenticate
                    let accessToken: string | undefined;
                    try {
                        accessToken = await driveService.getAccessToken();
                        
                        // #region agent log
                        fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuditioBackend.ts:1450',message:'getAudioData: getAccessToken succeeded',data:{hasToken:!!accessToken,tokenLength:accessToken?.length||0,driveFileId:audioFile.driveFileId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
                        // #endregion
                        console.log('✅ Got Google Drive access token (will use if public download fails)', {
                            hasToken: !!accessToken,
                            tokenLength: accessToken?.length || 0,
                            tokenPreview: accessToken ? accessToken.substring(0, 20) + '...' : 'missing',
                            driveFileId: audioFile.driveFileId,
                            timestamp: new Date().toISOString()
                        });
                    } catch (tokenError) {
                        // #region agent log
                        fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuditioBackend.ts:1458',message:'getAudioData: getAccessToken failed',data:{error:tokenError instanceof Error?tokenError.message:String(tokenError),driveFileId:audioFile.driveFileId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
                        // #endregion
                        
                        console.error('❌ Failed to get Google Drive access token:', {
                            error: tokenError instanceof Error ? tokenError.message : String(tokenError),
                            errorStack: tokenError instanceof Error ? tokenError.stack : undefined,
                            driveFileId: audioFile.driveFileId,
                            timestamp: new Date().toISOString()
                        });
                        // Check if it's a user cancellation vs actual error
                        const errorMessage = tokenError instanceof Error ? tokenError.message : String(tokenError);
                        if (errorMessage.includes('cancelled') || errorMessage.includes('Sign-in cancelled')) {
                            // User cancelled authentication - create a special error
                            const authCancelledError: any = new Error('Google Drive authentication was cancelled. Please try again and sign in to access your private files.');
                            authCancelledError.isAuthCancelled = true;
                            authCancelledError.isPermissionError = true;
                            authCancelledError.driveFileId = audioFile.driveFileId;
                            authCancelledError.driveFileUrl = audioFile.driveFileUrl;
                            throw authCancelledError;
                        } else {
                            // Other error - token might be missing or invalid
                            // Try public download first, but if that fails, we'll need auth
                            console.warn('⚠️ Could not get access token, will try public download first');
                            // #region agent log
                            fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuditioBackend.ts:1478',message:'getAudioData: Continuing without token, will try public download',data:{driveFileId:audioFile.driveFileId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
                            // #endregion
                            // Continue without token - backend will try public download
                        }
                    }
                    
                    // Call backend download endpoint
                    const baseUrl = this.getBaseUrl();
                    console.log('📤 Sending download request to backend:', {
                        driveFileId: audioFile.driveFileId,
                        hasAccessToken: !!accessToken,
                        endpoint: `${baseUrl}/drive/download`,
                        timestamp: new Date().toISOString()
                    });
                    
                    // #region agent log
                    fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuditioBackend.ts:1492',message:'getAudioData: Sending request to backend',data:{driveFileId:audioFile.driveFileId,hasAccessToken:!!accessToken,tokenLength:accessToken?.length||0},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
                    // #endregion
                    
                    const response = await fetch(`${baseUrl}/drive/download`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            driveFileId: audioFile.driveFileId,
                            accessToken: accessToken || undefined
                        })
                    });
                    
                    // #region agent log
                    fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuditioBackend.ts:1503',message:'getAudioData: Backend response received',data:{status:response.status,statusText:response.statusText,ok:response.ok,driveFileId:audioFile.driveFileId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
                    // #endregion
                    
                    if (!response.ok) {
                        // Parse error response to get more details
                        let errorDetails: any = {};
                        try {
                            errorDetails = await response.json();
                        } catch (e) {
                            // Response might not be JSON
                        }
                        
                        console.error('❌ Backend download failed:', {
                            status: response.status,
                            statusText: response.statusText,
                            driveFileId: audioFile.driveFileId,
                            hasAccessToken: !!accessToken,
                            errorDetails: errorDetails,
                            timestamp: new Date().toISOString()
                        });
                        
                        if (response.status === 401) {
                            // Invalid or expired token - user needs to re-authenticate
                            const authError: any = new Error('Your Google Drive session has expired. Please sign in again to access this file.');
                            authError.isAuthError = true;
                            authError.isPermissionError = true;
                            authError.driveFileId = audioFile.driveFileId;
                            authError.driveFileUrl = audioFile.driveFileUrl;
                            authError.requiresReauth = true;
                            throw authError;
                        } else if (response.status === 403) {
                            // Permission error - create a special error that includes file info
                            const permissionError: any = new Error(`Permission denied: You don't have access to this Google Drive file. Make sure you're signed in with the same Google account that owns the file.`);
                            permissionError.isPermissionError = true;
                            permissionError.driveFileId = audioFile.driveFileId;
                            permissionError.driveFileUrl = audioFile.driveFileUrl;
                            throw permissionError;
                        } else if (response.status === 404) {
                            // Check if the error indicates we need authentication
                            const errorMessage = errorDetails.error || errorDetails.details || 'Google Drive file not found';
                            const hint = errorDetails.hint || '';
                            
                            // If hint mentions access token or credentials, it's likely an auth issue
                            if (hint.includes('access token') || hint.includes('credentials') || hint.includes('different Google account')) {
                                const authRequiredError: any = new Error('This file requires Google Drive authentication. Please sign in to access your private files.');
                                authRequiredError.isAuthError = true;
                                authRequiredError.isPermissionError = true;
                                authRequiredError.requiresReauth = true;
                                authRequiredError.driveFileId = audioFile.driveFileId;
                                authRequiredError.driveFileUrl = audioFile.driveFileUrl;
                                throw authRequiredError;
                            }
                            
                            const notFoundError: any = new Error(`Google Drive file not found`);
                            notFoundError.isPermissionError = true;
                            notFoundError.driveFileId = audioFile.driveFileId;
                            notFoundError.driveFileUrl = audioFile.driveFileUrl;
                            throw notFoundError;
                        }
                        throw new Error(`Failed to fetch Drive file: ${response.status} ${response.statusText}`);
                    }
                    
                    // Convert response to blob, then to data URL
                    const blob = await response.blob();
                    
                    // Use the MIME type from the file or response, fallback to audio/mpeg
                    const mimeType = audioFile.driveMimeType || blob.type || 'audio/mpeg';
                    console.log('📦 Blob created:', {
                        size: blob.size,
                        type: blob.type,
                        driveMimeType: audioFile.driveMimeType,
                        usingMimeType: mimeType
                    });
                    
                    return new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            const dataUrl = reader.result as string;
                            console.log('✅ Google Drive file loaded successfully, size:', blob.size, 'bytes');
                            resolve(dataUrl);
                        };
                        reader.onerror = (error) => {
                            console.error('❌ FileReader error:', error);
                            reject(new Error('Failed to read Google Drive file'));
                        };
                        reader.readAsDataURL(blob);
                    });
                } catch (error) {
                    console.error('❌ Error loading Google Drive file:', error);
                    console.error('❌ Error details:', {
                        driveFileId: audioFile.driveFileId,
                        driveFileUrl: audioFile.driveFileUrl,
                        errorMessage: error instanceof Error ? error.message : String(error),
                        errorStack: error instanceof Error ? error.stack : undefined,
                        isAuthError: error && typeof error === 'object' && 'isAuthError' in error,
                        requiresReauth: error && typeof error === 'object' && 'requiresReauth' in error
                    });
                    // Re-throw permission/auth errors so they can be handled specially
                    if (error && typeof error === 'object' && ('isPermissionError' in error || 'isAuthError' in error || 'isAuthCancelled' in error)) {
                        throw error;
                    }
                    // Don't throw other errors - return null so the UI can handle it gracefully
                    return null;
                }
            }

            // Check if this is a YouTube file
            if (audioFile.youtubeUrl) {
                console.log('📥 YouTube file - no audio data to download');
                return null; // YouTube files are handled differently
            }

            console.log('📥 Audio data not in database, downloading from storage...');
            console.log('📁 File info:', {
                fileName: audioFile.fileName,
                localFilePath: audioFile.localFilePath,
                cloudFileName: audioFile.cloudFileName
            });

            // If no audio data, try to download from storage
            let fileName = audioFile.localFilePath || audioFile.cloudFileName;
            
            // If fileName is not available, construct it from the file ID and name
            if (!fileName) {
                fileName = `${audioFile.id}_${audioFile.fileName}`;
                console.log('🔧 Constructed fileName from ID and name:', fileName);
            }
            
            if (!fileName) {
                console.error('No file name available for download');
                return null;
            }

            const dataUrl = await this.downloadAudioFile(fileName);
            console.log('✅ Downloaded audio data from storage (length:', dataUrl.length, ')');
            return dataUrl;
        } catch (error) {
            console.error('Error getting audio data:', error);
            return null;
        }
    }

}